import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Input } from 'antd';
import chatStore from '../stores/chatStore';
import ChatListItem from './ChatListItem';
import { useTheme } from '../theme/ThemeContext';

function startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x.getTime();
}

function groupLabel(dateStr) {
    const d = new Date(dateStr || Date.now());
    const today = startOfDay(new Date());
    const day = startOfDay(d);
    const diff = (today - day) / 86400000;
    if (diff === 0) return 'Сегодня';
    if (diff === 1) return 'Вчера';
    if (diff < 7) return 'За последние 7 дней';
    if (diff < 30) return 'За последние 30 дней';
    return 'Ранее';
}

function groupByDate(chats) {
    const map = new Map();
    for (const chat of chats) {
        const label = groupLabel(chat.updated_at || chat.created_at);
        if (!map.has(label)) map.set(label, []);
        map.get(label).push(chat);
    }
    return Array.from(map.entries());
}

const SectionTitle = ({ children }) => (
    <div className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wide opacity-50">{children}</div>
);

const FolderBlock = observer(({ folder }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const collapsed = !!chatStore.collapsedFolderIds[folder.id];
    const items = chatStore.chatsInFolder(folder.id);

    const onRename = (e) => {
        e.stopPropagation();
        let value = folder.name || '';
        Modal.confirm({
            title: 'Переименовать папку',
            content: (
                <Input
                    defaultValue={value}
                    onChange={(ev) => {
                        value = ev.target.value;
                    }}
                />
            ),
            okText: 'Сохранить',
            cancelText: 'Отмена',
            onOk: async () => {
                if (value.trim()) await chatStore.renameFolder(folder.id, value.trim());
            },
        });
    };

    const onDelete = (e) => {
        e.stopPropagation();
        Modal.confirm({
            title: 'Удалить папку?',
            content: 'Чаты из папки останутся в списке без папки.',
            okText: 'Удалить',
            okType: 'danger',
            cancelText: 'Отмена',
            onOk: async () => chatStore.removeFolder(folder.id),
        });
    };

    return (
        <div className="mb-3">
            <button
                type="button"
                className={`group flex w-full items-center gap-1 rounded-lg px-2 py-1.5 text-left ${
                    isDark ? 'hover:bg-white/5' : 'hover:bg-black/5'
                }`}
                onClick={() => chatStore.toggleFolderCollapsed(folder.id)}
            >
                <span className="w-3 text-[10px] opacity-50">{collapsed ? '▸' : '▾'}</span>
                <span className="opacity-70">📁</span>
                <span className="bananas-folder-font min-w-0 flex-1 truncate">{folder.name}</span>
                <span className="text-[10px] opacity-40">{items.length}</span>
                <span className="ml-1 hidden items-center gap-0.5 group-hover:inline-flex">
                    <button
                        type="button"
                        className={`rounded px-1 text-[11px] opacity-60 hover:opacity-100 ${
                            isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'
                        }`}
                        onClick={onRename}
                        title="Переименовать"
                    >
                        ✎
                    </button>
                    <button
                        type="button"
                        className={`rounded px-1 text-[11px] text-red-500 opacity-60 hover:opacity-100 ${
                            isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'
                        }`}
                        onClick={onDelete}
                        title="Удалить папку"
                    >
                        ×
                    </button>
                </span>
            </button>
            {!collapsed && (
                <div className="ml-2 space-y-0.5 border-l border-black/5 pl-1 dark:border-white/10">
                    {items.length ? (
                        items.map((chat) => <ChatListItem key={chat.id} chat={chat} />)
                    ) : (
                        <div className="px-3 py-2 text-[11px] opacity-40">Пусто</div>
                    )}
                </div>
            )}
        </div>
    );
});

const ChatList = observer(() => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const pinned = chatStore.pinnedChats;
    const root = chatStore.rootChats;
    const folders = chatStore.folders;
    const searching = !!chatStore.searchQuery.trim();

    const rootGroups = useMemo(
        () => groupByDate(root),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [root, chatStore.searchQuery, chatStore.chats.length]
    );

    const onCreateFolder = () => {
        let value = 'Новая папка';
        Modal.confirm({
            title: 'Новая папка',
            content: (
                <Input
                    defaultValue={value}
                    onChange={(e) => {
                        value = e.target.value;
                    }}
                />
            ),
            okText: 'Создать',
            cancelText: 'Отмена',
            onOk: async () => {
                await chatStore.createFolder(value.trim() || 'Новая папка');
            },
        });
    };

    if (!chatStore.filteredChats.length && !folders.length) {
        return <div className="px-2 py-6 text-center text-xs opacity-60">Нет диалогов</div>;
    }

    if (searching) {
        const results = chatStore.filteredChats;
        if (!results.length) {
            return <div className="px-2 py-6 text-center text-xs opacity-60">Ничего не найдено</div>;
        }
        return (
            <div className="space-y-0.5">
                {results.map((chat) => (
                    <ChatListItem key={chat.id} chat={chat} />
                ))}
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {pinned.length > 0 && (
                <div>
                    <SectionTitle>Закреплённые</SectionTitle>
                    <div className="space-y-0.5">
                        {pinned.map((chat) => (
                            <ChatListItem key={chat.id} chat={chat} />
                        ))}
                    </div>
                </div>
            )}

            <div>
                <SectionTitle>Папки</SectionTitle>
                <div className="px-1 pb-1.5">
                    <button
                        type="button"
                        onClick={onCreateFolder}
                        className={`bananas-list-font flex w-full items-center justify-center gap-1.5 rounded-lg border px-3 py-2 font-medium transition ${
                            isDark
                                ? 'border-white/15 bg-white/5 text-white/85 hover:bg-white/10 hover:text-white'
                                : 'border-black/10 bg-white text-gray-800 shadow-sm hover:bg-black/[0.03]'
                        }`}
                    >
                        <span className="text-[1.1em] leading-none" aria-hidden>
                            +
                        </span>
                        Новая папка
                    </button>
                </div>
                {folders.length > 0 ? (
                    <div className="space-y-1">
                        {folders.map((folder) => (
                            <FolderBlock key={folder.id} folder={folder} />
                        ))}
                    </div>
                ) : (
                    <div className="px-3 py-1 text-[11px] opacity-40">Нет папок</div>
                )}
            </div>

            {rootGroups.length > 0 && (
                <div className="space-y-4">
                    {rootGroups.map(([label, items]) => (
                        <div key={label}>
                            <SectionTitle>{label}</SectionTitle>
                            <div className="space-y-0.5">
                                {items.map((chat) => (
                                    <ChatListItem key={chat.id} chat={chat} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

export default ChatList;
